import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@iconify/react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import MuiConfirmationDialog from "../../@material-extend/MuiConfirmationDialog";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { alertSlice } from "src/store/alert/alert.slice";
import enums, { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import {
  getTeamUrl,
  fleetSessionsReconcileUrl,
} from "src/react-query/endPoints";
import {
  toList,
  listTotal,
  statusChipColor,
  formatMoney,
  formatKwh,
} from "../fleet.constants";

const getSessionId = (row) =>
  row?.fleet_session_id ?? row?.fleetSessionId ?? row?.id;

const SummaryCard = ({ title, value, caption }) => (
  <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h5">{value}</Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Card>
);

const SessionsTab = ({ operatorId }) => {
  const dispatch = useDispatch();
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");

  const [filters, setFilters] = useState({ from: "", to: "" });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const rangeParams = useMemo(
    () => ({
      ...(filters.from && { from: filters.from }),
      ...(filters.to && { to: filters.to }),
    }),
    [filters]
  );

  const {
    data: summary,
    isFetching: isSummaryFetching,
    refetch: refetchSummary,
  } = useQuery(
    queryKeys.fleetReportSummary(teamId, operatorId, rangeParams),
    fetchWithError,
    {
      enabled: Boolean(teamId && operatorId),
      select: selector,
      keepPreviousData: true,
    }
  );

  const {
    data: sessionsData,
    isFetching: isSessionsFetching,
    refetch: refetchSessions,
  } = useQuery(
    queryKeys.fleetSessions(teamId, operatorId, {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      ...rangeParams,
    }),
    fetchWithError,
    {
      enabled: Boolean(teamId && operatorId),
      select: selector,
      keepPreviousData: true,
    }
  );

  const sessions = useMemo(() => toList(sessionsData), [sessionsData]);
  const sessionsTotal = listTotal(sessionsData, sessions.length);
  const byDriver = useMemo(() => toList(summary?.byDriver), [summary]);
  const byVehicle = useMemo(() => toList(summary?.byVehicle), [summary]);
  const acDc = summary?.acDc ?? {};

  const { mutate: reconcile, isLoading: isReconciling } = useMutate(
    "fleetSessions",
    { id: operatorId }
  );

  const onReconcile = () => {
    reconcile(
      {
        url: getTeamUrl(teamId, fleetSessionsReconcileUrl),
        method: "POST",
      },
      {
        onSuccess: (response) => {
          setReconcileOpen(false);
          const result = response?.data ?? {};
          dispatch(
            alertSlice.actions.setAlert({
              open: true,
              severity: enums.SEVERITY.SUCCESS,
              message: `Reconciliation complete — captured: ${
                result.captured ?? 0
              }, debited: ${result.debited ?? 0}, skipped: ${
                result.skipped ?? 0
              }`,
            })
          );
          refetchSessions();
          refetchSummary();
        },
      }
    );
  };

  const sessionColumns = useMemo(
    () => [
      {
        field: "started_at",
        headerName: "Started",
        flex: 1,
        renderCell: ({ value }) => fDateTime(value) || "-",
      },
      {
        field: "charger_id",
        headerName: "Charger",
        flex: 0.9,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "driver",
        headerName: "Driver",
        flex: 0.9,
        sortable: false,
        renderCell: ({ row }) => {
          const name =
            row?.driver_name ??
            [row?.first_name, row?.last_name].filter(Boolean).join(" ");
          return name || (row?.driver_id ? `#${row.driver_id}` : "-");
        },
      },
      {
        field: "vehicle",
        headerName: "Vehicle",
        flex: 0.9,
        sortable: false,
        renderCell: ({ row }) =>
          row?.registration_number ??
          (row?.vehicle_id ? `#${row.vehicle_id}` : "-"),
      },
      {
        field: "energy_kwh",
        headerName: "Energy",
        flex: 0.7,
        renderCell: ({ value }) => formatKwh(value),
      },
      {
        field: "amount",
        headerName: "Amount",
        flex: 0.7,
        renderCell: ({ row, value }) => formatMoney(value, row?.currency),
      },
      {
        field: "charger_power_type",
        headerName: "AC/DC",
        flex: 0.5,
        renderCell: ({ value }) =>
          value ? <Chip size="small" variant="outlined" label={value} /> : "-",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        renderCell: ({ value }) => (
          <Chip size="small" label={value ?? "-"} color={statusChipColor(value)} />
        ),
      },
    ],
    []
  );

  const byDriverColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Driver",
        flex: 1,
        renderCell: ({ row, value }) =>
          value ?? (row?.driverId ? `#${row.driverId}` : "-"),
      },
      { field: "sessions", headerName: "Sessions", flex: 0.5 },
      {
        field: "kwh",
        headerName: "Energy",
        flex: 0.6,
        renderCell: ({ value }) => formatKwh(value),
      },
      {
        field: "amount",
        headerName: "Spend",
        flex: 0.6,
        renderCell: ({ value }) => formatMoney(value),
      },
    ],
    []
  );

  const byVehicleColumns = useMemo(
    () => [
      {
        field: "registration_number",
        headerName: "Vehicle",
        flex: 1,
        renderCell: ({ row, value }) =>
          value ?? (row?.vehicleId ? `#${row.vehicleId}` : "-"),
      },
      { field: "sessions", headerName: "Sessions", flex: 0.5 },
      {
        field: "kwh",
        headerName: "Energy",
        flex: 0.6,
        renderCell: ({ value }) => formatKwh(value),
      },
      {
        field: "amount",
        headerName: "Spend",
        flex: 0.6,
        renderCell: ({ value }) => formatMoney(value),
      },
      {
        field: "batteryCycleEstimate",
        headerName: "Battery Cycles (est.)",
        flex: 0.8,
        renderCell: ({ value }) =>
          value !== null && value !== undefined ? Number(value).toFixed(2) : "-",
      },
    ],
    []
  );

  const acDcCaption = `AC ${acDc?.AC?.sessions ?? 0} · DC ${
    acDc?.DC?.sessions ?? 0
  } · Unknown ${acDc?.UNKNOWN?.sessions ?? 0}`;

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mb={2}
      >
        <Typography variant="h6">Sessions &amp; Reports</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, from: e.target.value }));
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, to: e.target.value }));
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          />
          {hasUpdateAccess && !isScopedOperator && (
            <LoadingButton
              variant="outlined"
              startIcon={<Icon icon="eva:refresh-outline" />}
              loading={isReconciling}
              onClick={() => setReconcileOpen(true)}
            >
              Reconcile Sessions
            </LoadingButton>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Sessions"
            value={isSummaryFetching ? "-" : summary?.totalSessions ?? 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Energy"
            value={isSummaryFetching ? "-" : formatKwh(summary?.totalEnergyKwh ?? 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Spend"
            value={isSummaryFetching ? "-" : formatMoney(summary?.totalAmount ?? 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="AC / DC Split"
            value={
              isSummaryFetching
                ? "-"
                : `${formatKwh(acDc?.AC?.kwh ?? 0)} / ${formatKwh(
                    acDc?.DC?.kwh ?? 0
                  )}`
            }
            caption={isSummaryFetching ? "" : acDcCaption}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" mb={1}>
        Charging Sessions
      </Typography>
      <BaseTable
        rows={sessions}
        columns={sessionColumns}
        loading={isSessionsFetching}
        getRowId={(row) => getSessionId(row)}
        pageSize={paginationModel.pageSize}
        serverRowFetcher
        totalRows={sessionsTotal}
        paginationModel={paginationModel}
        onPageChange={(page) =>
          setPaginationModel((prev) => ({ ...prev, page }))
        }
        onPageSizeChange={(pageSize) =>
          setPaginationModel((prev) => ({ ...prev, pageSize, page: 0 }))
        }
      />
      {!isSessionsFetching && !sessions.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No charging sessions in the selected period.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" mb={1}>
            By Driver
          </Typography>
          <BaseTable
            rows={byDriver}
            columns={byDriverColumns}
            loading={isSummaryFetching}
            getRowId={(row) => row?.driverId ?? row?.driver_id ?? row?.name}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
          />
          {!isSummaryFetching && !byDriver.length && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 2 }}
            >
              No per-driver data.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" mb={1}>
            By Vehicle
          </Typography>
          <BaseTable
            rows={byVehicle}
            columns={byVehicleColumns}
            loading={isSummaryFetching}
            getRowId={(row) =>
              row?.vehicleId ?? row?.vehicle_id ?? row?.registration_number
            }
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
          />
          {!isSummaryFetching && !byVehicle.length && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 2 }}
            >
              No per-vehicle data.
            </Typography>
          )}
        </Grid>
      </Grid>

      <MuiConfirmationDialog
        open={reconcileOpen}
        onClose={() => setReconcileOpen(false)}
        title="Reconcile Sessions"
        severity="info"
        contentText="Scans completed charging transactions for fleet RFID tags, captures missing fleet sessions and debits wallets where applicable. This is safe to run repeatedly."
        closeText="Cancel"
        okText="Run Reconciliation"
        onOk={onReconcile}
        isLoading={isReconciling}
      />
    </>
  );
};

export default SessionsTab;
